// Saves a media URL as a file. The media CDN's CORS allows the app's origin, so the file is
// fetched as a blob (no header of ours) and handed to the browser as a download: an
// `<a download>` on a cross-origin URL would only open it. A signed URL goes without credentials;
// a file's own URL, which a browser holding the team's media cookie reads (spec
// assets/media-cookies), goes with them, so the cookie reaches the CDN.
import { mediaCredentials } from "./media-cookie";

/** Why a download did not start: the file could not be read, or the CDN refused it. */
export class DownloadError extends Error {
  override readonly name = "DownloadError";
  /** The CDN's status, or `undefined` when the request did not complete (offline, CORS). */
  readonly status: number | undefined;

  constructor(status?: number, options?: ErrorOptions) {
    super(status === undefined ? "Download failed" : `Download failed, status ${status}`, options);
    this.status = status;
  }
}

/**
 * Fetches a media URL whole, with no header of ours, and credentials only for a file's own URL
 * (`mediaCredentials`). Throws `DownloadError` when it cannot: offline, refused by CORS, or a
 * status that is not 2xx.
 */
export async function fetchSignedFile(url: string, signal?: AbortSignal): Promise<Blob> {
  let response: Response;
  try {
    const credentials = mediaCredentials(url);
    response = await fetch(url, { credentials, ...(signal === undefined ? {} : { signal }) });
  } catch (cause) {
    if (signal?.aborted === true) throw cause;
    throw new DownloadError(undefined, { cause });
  }
  if (!response.ok) throw new DownloadError(response.status);
  return response.blob();
}

/** Hands a file the page holds to the browser as a download named `filename`. */
export function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  try {
    link.click();
  } finally {
    link.remove();
    // The click has handed the blob to the browser; free it once that turn is over.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

/** Fetches `url` and saves it as `filename`. Throws `DownloadError` when it cannot. */
export async function downloadSignedFile(url: string, filename: string): Promise<void> {
  saveBlob(await fetchSignedFile(url), filename);
}

/** A fetch for a media file's ranged reads, such as `stoppableFetch` makes. */
export type RangeFetch = typeof fetch;

/**
 * The fetch for a media file's ranged reads (the editor's scrub cache, through mediabunny): as
 * `fetch`, but each read also stops once the signal `stop()` gives then aborts, so the preview stops
 * its reads ahead at once when it plays, leaving the file's own element the network (spec
 * stalled-element).
 */
export function stoppableFetch(stop: () => AbortSignal): RangeFetch {
  return (input, init) => {
    // Either signal stops it, the caller's own or `stop()`'s, with its reason: AbortSignal.any by
    // hand, which Safari lacks before 17.4 while it decodes video (WebCodecs).
    const read = new AbortController();
    for (const signal of [stop(), init?.signal]) {
      if (signal === undefined || signal === null) continue;
      if (signal.aborted) read.abort(signal.reason);
      else signal.addEventListener("abort", () => read.abort(signal.reason), { once: true });
    }
    return fetch(input, { ...init, signal: read.signal });
  };
}
